import { type Jsonify } from 'type-fest';

import { toJson } from '@frontend/utils';

type ValueFactory<K, V> = (k?: K) => V;
type DisposeValue<V> = (v: V) => void;

abstract class BaseCacheMap<K, V> {
  protected abstract _m: Pick<Map<K, V>, 'get' | 'has' | 'delete'> & {
    set(k: K, v: V): unknown; //returned value won't be used (don't care if it is WeakMap or Map which have different `this` type)
    readonly size?: number; //only defined for Map
  };

  /*
   * @param valueFactory - used by get() to create value if it does not exist
   * @param disposeValue - used by delete() and clear() to dipose of value before it is deleted
   * @param deleteFromParent - used by delete() and clear() to delete this CacheMap/CacheWeakMap from parent if the cache is empty
   */
  constructor(
    private valueFactory: ValueFactory<K, V>,
    protected disposeValue?: DisposeValue<V>,
    protected deleteFromParent?: () => void,
  ) {}

  /*
   * returns true if cache has entry for key k
   */
  public has(k: K): boolean {
    return this._m.has(k);
  }

  /*
   * Get item from cache or create it, cache it and return it
   * @remarks Acts like a getter if the entry exists or a factory/getter if it does not exist
   */
  public get(k: K): V {
    if (this._m.has(k)) {
      return this._m.get(k) as V;
    }
    const v = this.valueFactory(k);
    this._m.set(k, v);
    return v;
  }

  //Setter is intentionally not implemented. Use `this.get()` which will create, cache and set if required
  //public set(): this {
  //  throw new Error('Intentionally not implemented.');
  //}

  /*
   * Delete item from cache and if disposeValue() provided, disposes first
   */
  public delete(k: K, dispose = true): boolean {
    if (dispose && this.disposeValue && this._m.has(k)) {
      this.disposeValue(this._m.get(k) as V);
    }
    const deleted = this._m.delete(k);
    if (this.deleteFromParent) {
      const size = this._m.size;
      if (size === undefined || size === 0) {
        this.deleteFromParent();
      }
    }
    return deleted;
  }
}

export class CacheMap<K, V> extends BaseCacheMap<K, V> {
  protected _m: Map<K, V>;

  constructor(
    valueFactory: ValueFactory<K, V>,
    disposeValue?: DisposeValue<V>,
    deleteFromParent?: () => void,
    entries: Iterable<[K, V]> = [],
  ) {
    super(valueFactory, disposeValue, deleteFromParent);
    this._m = new Map(entries);
  }

  public get size(): number {
    return this._m.size;
  }

  /*
   * Clear items from CacheMap
   */
  public clear(dispose = true): void {
    if (dispose && this.disposeValue) {
      for (const v of this._m.values()) {
        this.disposeValue(v);
      }
    }
    this._m.clear();
    if (this.deleteFromParent) {
      this.deleteFromParent();
    }
  }

  /*
   * Returns an iterable of keys in the CacheMap
   */
  public keys(): IterableIterator<K> {
    return this._m.keys();
  }

  /*
   * Returns an iterable of values in the CacheMap
   */
  public values(): IterableIterator<V> {
    return this._m.values();
  }

  /*
   * Returns an iterable of entries [key, value] in the CacheMap
   */
  public entries(): IterableIterator<[K, V]> {
    return this._m.entries();
  }

  /*
   * Returns JSON entries for CacheMap
   */
  public toJSON(): [Jsonify<K>, Jsonify<V>][] {
    return toJson(this._m);
  }

  /*
   * Returns an iterable of entries [key, value] in the CacheMap
   */
  [Symbol.iterator](): IterableIterator<[K, V]> {
    return this._m[Symbol.iterator]();
  }
}

export class CacheWeakMap<K extends object, V> extends BaseCacheMap<K, V> {
  protected _m: WeakMap<K, V>;

  constructor(
    valueFactory: ValueFactory<K, V>,
    disposeValue?: DisposeValue<V>,
    deleteFromParent?: () => void,
    entries: Iterable<[K, V]> = [],
  ) {
    super(valueFactory, disposeValue, deleteFromParent);
    this._m = new WeakMap(entries);
  }
}

//Note: At this time, I see no need for nestedCacheWeakMapFactory(). I think it only makes sense to have a WeakMap for the first level

/*
 * nestedCacheMapFactory() is a helper to create nested CacheMaps, which is tedious to do correctly with dispose() doing appropriate cleanup
 */
export const nestedCacheMapFactory = <K, V, Vend>(
  depth: number,
  leafValueFactory: () => Vend,
  leafDisposeValue?: DisposeValue<Vend>,
  deleteFromParent?: () => void,
): CacheMap<K, V> => {
  if (depth < 1 || !Number.isSafeInteger(depth)) {
    throw new Error('Unexpected depth for nested CacheMap');
  }
  const result: CacheMap<K, V> = new CacheMap<K, V>(
    //Factory for next inner CacheMap
    (depth > 1
      ? (key: K) =>
          nestedCacheMapFactory<unknown, unknown, Vend>(
            depth - 1,
            leafValueFactory,
            leafDisposeValue,
            // deleteFromParent fn (called by CacheMap.prototype.delete() and CacheMap.prototype.clear() if the CacheMap is empty (cleans itself up from parent result))
            () => {
              result.delete(key);
            },
          )
      : () => leafValueFactory()) as unknown as ValueFactory<K, V>, //Kend === K if depth === 1 //By inspection, the V === `CacheMap<K, unknown> | Vend`
    //disposeValue
    (depth > 1
      ? (v: CacheMap<unknown, unknown>) => {
          v.clear();
        } //v must be a CacheMap because depth > 1
      : leafDisposeValue) as DisposeValue<unknown> | undefined, //By inspection, the unknown value being disposed is either `Vend | CacheMap<unknown, unknown>`
    deleteFromParent,
  );
  return result;
};
